import { APP_ROUTES, normalizePathname } from '../../navigation/appRoutes';
import type { CanOptions } from '../permissionLogic';
import type { NormalizedResourcePermission, PermissionContext } from '../types';
import { canShowSchedulerNav, hasAnySchedulerViewAccess } from './navAccess';
import {
  SCHEDULER_NAV_PERMISSIONS,
  type SchedulerNavPermission,
} from './resources';

const LANDING_PATHS: { nav: SchedulerNavPermission; path: string }[] = [
  { nav: SCHEDULER_NAV_PERMISSIONS.dashboardOverview, path: APP_ROUTES.dashboard.overview },
  { nav: SCHEDULER_NAV_PERMISSIONS.reportsLocationHealth, path: APP_ROUTES.reports.locationHealth },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleImport, path: APP_ROUTES.schedule.import },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleLocations, path: APP_ROUTES.schedule.locations },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleTasks, path: APP_ROUTES.schedule.tasks },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleBaselines, path: APP_ROUTES.schedule.baselines },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleCalendar, path: APP_ROUTES.schedule.calendar },
  { nav: SCHEDULER_NAV_PERMISSIONS.scheduleTasks, path: APP_ROUTES.schedule.resources },
];

/** Request tracker is intentionally outside the permission catalog. */
export function isPermissionExemptPath(pathname: string): boolean {
  return normalizePathname(pathname) === APP_ROUTES.requestTracking;
}

/** Maps current URL to the nav permission used for route guards */
export function getSchedulerRouteNavPermission(
  pathname: string
): SchedulerNavPermission | null {
  const p = normalizePathname(pathname);

  if (isPermissionExemptPath(p)) {
    return null;
  }
  if (p === APP_ROUTES.dashboard.overview || p === '/dashboard') {
    return SCHEDULER_NAV_PERMISSIONS.dashboardOverview;
  }
  if (
    p === APP_ROUTES.reports.locationHealth ||
    p === APP_ROUTES.reports.dependencyBlockers ||
    p === APP_ROUTES.reports.sCurve ||
    p === '/reports' ||
    p === APP_ROUTES.dashboard.reports
  ) {
    return SCHEDULER_NAV_PERMISSIONS.reportsLocationHealth;
  }
  if (p === APP_ROUTES.schedule.import || p === '/schedule') {
    return SCHEDULER_NAV_PERMISSIONS.scheduleImport;
  }
  if (p === APP_ROUTES.schedule.locations) {
    return SCHEDULER_NAV_PERMISSIONS.scheduleLocations;
  }
  if (p === APP_ROUTES.schedule.tasks) {
    return SCHEDULER_NAV_PERMISSIONS.scheduleTasks;
  }
  if (p === APP_ROUTES.schedule.baselines) {
    return SCHEDULER_NAV_PERMISSIONS.scheduleBaselines;
  }
  if (p === APP_ROUTES.schedule.calendar) {
    return SCHEDULER_NAV_PERMISSIONS.scheduleCalendar;
  }
  if (p === APP_ROUTES.schedule.resources) {
    return SCHEDULER_NAV_PERMISSIONS.scheduleTasks;
  }

  return null;
}

export function canAccessSchedulerPath(
  index: Map<string, NormalizedResourcePermission>,
  pathname: string,
  context: PermissionContext,
  options: { ready: boolean }
): boolean {
  if (!options.ready) {
    return false;
  }

  // Always allow request tracker (no resource grant required).
  if (isPermissionExemptPath(pathname)) {
    return true;
  }

  if (
    !hasAnySchedulerViewAccess(index, context, {
      ready: true,
      failClosed: true,
    })
  ) {
    return false;
  }

  const routeNav = getSchedulerRouteNavPermission(pathname);
  if (!routeNav) {
    // Unknown / legacy / utility paths under AppLayout — allow if module access exists
    return true;
  }

  return canShowSchedulerNav(index, routeNav, context, { ready: true });
}

/** First URL the user is allowed to open when landing on `/` */
export function getDefaultSchedulerLandingPath(
  index: Map<string, NormalizedResourcePermission>,
  context: PermissionContext
): string | null {
  const opts: CanOptions = { ready: true };

  for (const { nav, path } of LANDING_PATHS) {
    if (canShowSchedulerNav(index, nav, context, opts)) {
      return path;
    }
  }

  return null;
}
